// pages/MonthlyReport.jsx
// "Monthly Report" page - split out of the tab that used to live in
// TableMaintenance.jsx.
// Permission: 'monthly_report' (see AVAILABLE_PERMISSIONS in backend/database.js).
//
// UI primitives and CronScheduler come from tmShared.jsx so nothing is
// duplicated with TableMaintenance.jsx.
//
// All user-visible strings go through t('mreport.*') (see context/translations.js,
// languages id + en; ar intentionally falls back to en).

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { Alert, Btn, Empty, useApi, fmtBytes, CronScheduler } from './tmShared';
import useTabDeepLink from '../hooks/useTabDeepLink';
import SapUsagePeriodicPanel from './SapUsagePeriodicPanel';
import VpnDailyReportPanel from './VpnDailyReportPanel';

// ── Editor pengantar ─────────────────────────────────────────────────────────
// Sengaja BUKAN WYSIWYG (contentEditable). Yang tersimpan adalah teks biasa
// berisi penanda, bukan HTML — sehingga jaminan di backend tetap utuh: isinya
// di-escape lebih dulu, baru penandanya diterjemahkan jadi tag. Tidak ada
// allowlist sanitizer yang bisa salah, dan tempelan dari Word/Outlook masuk
// sebagai teks bersih, bukan sebagai <span style="mso-...">.
//
// ⚠ SINKRON DENGAN backend: ketiga pola di bawah harus sama persis dengan
// INTRO_MARKS di monthlyReport.js. Kalau berbeda, pratinjau di sini akan
// berbohong tentang isi email yang benar-benar terkirim.
const MARKS = [
  [/\*\*([^\n*][^\n*]*?)\*\*/g, '<b>$1</b>'],
  [/__([^\n_][^\n_]*?)__/g,     '<u>$1</u>'],
  [/\*([^\n*][^\n*]*?)\*/g,     '<i>$1</i>'],
];
// Kode inline: `teks`. Diproses TERPISAH dari MARKS (mekanismenya harus sama
// persis dengan CODE_RE/maskCodeSpans di monthlyReport.js — lihat komentar di
// sana untuk alasannya: isi code span harus DILINDUNGI dari bold/italic,
// bukan diproses sesudahnya, supaya glob/path seperti `*.js -> *.ts` tidak
// tertangkap regex italic).
const CODE_RE = /`([^`\n]+?)`/g;
const CODE_STYLE = "font-family:'Courier New',Consolas,monospace;background:#f8fafc;"
  + "border:1px solid #e5e7eb;border-radius:3px;padding:1px 5px;font-size:0.92em;";
function maskCodeSpans(text) {
  const spans = [];
  const masked = text.replace(CODE_RE, (_, inner) => {
    const i = spans.push(inner) - 1;
    return `\u0000C${i}\u0000`;
  });
  return { masked, spans };
}
const UNMASK_RE = /\u0000C(\d+)\u0000/g;
const SPACING_CSS = { tight: 1.35, normal: 1.5, relaxed: 1.65 };
const SPACING_GAP = { tight: 6, normal: 9, relaxed: 12 };

const escHtml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Cerminan introToHtml() di backend: baris tunggal = <br>, baris kosong =
// paragraf. Escape SELALU lebih dulu, sama seperti di sana.
function introPreviewHtml(text, spacing) {
  const blocks = String(text || '').split(/\n\s*\n+/).map(x => x.trim()).filter(Boolean);
  if (!blocks.length) return '';
  const lh = SPACING_CSS[spacing] || SPACING_CSS.normal;
  const gap = SPACING_GAP[spacing] || SPACING_GAP.normal;
  return blocks.map((blk, i) => {
    const body = blk.split('\n')
      .map(line => {
        const { masked, spans } = maskCodeSpans(escHtml(line.trim()));
        const styled = MARKS.reduce((acc, [re, rep]) => acc.replace(re, rep), masked);
        return styled.replace(UNMASK_RE, (_, k) => `<code style="${CODE_STYLE}">${spans[Number(k)]}</code>`);
      })
      .join('<br />');
    return `<div style="line-height:${lh};${i ? `margin-top:${gap}px;` : ''}">${body}</div>`;
  }).join('');
}

function IntroEditor({ value, spacing, onChange, onSpacing, t, inputStyle, labelStyle }) {
  const ref = useRef(null);

  // Membungkus seleksi dengan penanda. Kalau tidak ada yang diseleksi,
  // penandanya tetap disisipkan dan kursor ditaruh DI ANTARA keduanya — supaya
  // tombol tetap berguna sebelum mengetik, bukan hanya sesudahnya.
  const wrap = useCallback((mark) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const sel = value.slice(a, b);
    const next = value.slice(0, a) + mark + sel + mark + value.slice(b);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + mark.length, a + mark.length + sel.length);
    });
  }, [value, onChange]);

  // Daftar: memberi awalan pada SETIAP baris yang tersentuh seleksi, bukan hanya
  // baris pertama. Tanpa itu, memblok lima baris lalu menekan tombol hanya
  // menomori satu — dan itu terlihat seperti tombol yang rusak.
  const listify = useCallback((kind) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const from = value.lastIndexOf('\n', a - 1) + 1;
    const toRaw = value.indexOf('\n', b);
    const to = toRaw === -1 ? value.length : toRaw;
    const lines = value.slice(from, to).split('\n');
    let n = 0;
    const out = lines.map((ln) => {
      const bare = ln.replace(/^\s*(?:\d+\.\s+|•\s+)/, '');
      if (!bare.trim()) return ln;
      n += 1;
      return kind === 'ol' ? `${n}. ${bare}` : `• ${bare}`;
    }).join('\n');
    const next = value.slice(0, from) + out + value.slice(to);
    onChange(next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(from, from + out.length); });
  }, [value, onChange]);

  const clearFmt = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const target = a === b ? value : value.slice(a, b);
    // Sama seperti introToPlain di backend: lindungi isi code span dari MARKS
    // dulu, baru strip bold/italic/underline, baru kembalikan isi code apa
    // adanya. Tanpa ini, `*.js -> *.ts` bisa kehilangan tanda bintangnya kalau
    // MARKS diproses langsung di atas teks yang backtick-nya belum dilepas.
    const { masked, spans } = maskCodeSpans(target);
    const strippedMasked = MARKS.reduce((acc, [re]) => acc.replace(re, '$1'), masked);
    const stripped = strippedMasked.replace(UNMASK_RE, (_, k) => spans[Number(k)])
      .replace(/^\s*(?:\d+\.\s+|•\s+)/gm, '');
    const next = a === b ? stripped : value.slice(0, a) + stripped + value.slice(b);
    onChange(next);
    requestAnimationFrame(() => el.focus());
  }, [value, onChange]);

  const preview = useMemo(() => introPreviewHtml(value, spacing), [value, spacing]);

  const btn = {
    padding: '4px 9px', fontSize: 12, lineHeight: 1.2, cursor: 'pointer',
    border: '1px solid #cbd5e1', borderRadius: 5, background: '#fff',
    color: '#334155', fontFamily: 'inherit', minWidth: 30,
  };
  const tools = [
    { k: 'b',  lab: <b>B</b>,             tip: t('mreport.intro.fmt.bold'),     run: () => wrap('**') },
    { k: 'i',  lab: <i>I</i>,             tip: t('mreport.intro.fmt.italic'),   run: () => wrap('*') },
    { k: 'u',  lab: <u>U</u>,             tip: t('mreport.intro.fmt.underline'), run: () => wrap('__') },
    { k: 'c',  lab: <code style={{ fontSize: 11 }}>{'</>'}</code>,
                                          tip: t('mreport.intro.fmt.code'),     run: () => wrap('`') },
    { k: 'ol', lab: '1.',                 tip: t('mreport.intro.fmt.numbered'), run: () => listify('ol') },
    { k: 'ul', lab: '•',                  tip: t('mreport.intro.fmt.bullet'),   run: () => listify('ul') },
    { k: 'x',  lab: '⌫',                  tip: t('mreport.intro.fmt.clear'),    run: clearFmt },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        {tools.map(x => (
          <button key={x.k} type="button" title={x.tip} onClick={x.run} style={btn}>{x.lab}</button>
        ))}
        <span style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 2px' }} />
        <span style={{ fontSize: 11, color: '#64748b' }}>{t('mreport.intro.spacing')}</span>
        <select value={spacing} onChange={e => onSpacing(e.target.value)}
          style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}>
          <option value="tight">{t('mreport.intro.spacing.tight')}</option>
          <option value="normal">{t('mreport.intro.spacing.normal')}</option>
          <option value="relaxed">{t('mreport.intro.spacing.relaxed')}</option>
        </select>
      </div>

      <textarea ref={ref} value={value} rows={6} maxLength={4000}
        placeholder={t('mreport.intro.ph')}
        onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle, width: 680, maxWidth: '100%', resize: 'vertical',
                 lineHeight: 1.6, display: 'block', fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                 fontSize: 12.5 }} />

      {/* Pratinjau. Aman memakai dangerouslySetInnerHTML karena HTML-nya bukan
          dari server maupun dari isi mentah: teksnya di-escape lebih dulu, lalu
          hanya <b>/<i>/<u>/<br> yang disisipkan oleh introPreviewHtml. */}
      <div style={{ marginTop: 8 }}>
        <div style={{ ...labelStyle, marginBottom: 3 }}>{t('mreport.intro.preview')}</div>
        <div style={{ border: '1px dashed #cbd5e1', borderRadius: 6, padding: '10px 12px',
                      background: '#fff', width: 680, maxWidth: '100%',
                      fontFamily: '"Aptos Narrow", "Segoe UI", Arial, sans-serif',
                      fontSize: 14, color: '#1f2937', minHeight: 24 }}>
          {value.trim()
            ? <div dangerouslySetInnerHTML={{ __html: preview }} />
            : <span style={{ color: '#cbd5e1', fontSize: 12 }}>{t('mreport.intro.previewEmpty')}</span>}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
          {t('mreport.intro.previewNote')}
        </div>
      </div>
    </div>
  );
}

// Two switches that are easy to mix up:
//   - CronScheduler (Enable)          -> cron_settings.enabled: does the SCHEDULE run
//   - "Enable automatic delivery"     -> monthly_report_profiles.enabled: does
//                                        runReport actually send when cron calls it
// Both must be on for automatic delivery. "Send now" and "Run Now" use a manual
// trigger and are therefore NOT affected by the second switch.
function MonthlyReportPanel({ api, type, onChanged }) {
  const { t } = useLang();
  const [cfg, setCfg]       = useState(null);
  const [form, setForm]     = useState(null);
  const [alert, setAlert]   = useState(null);
  const [busy, setBusy]     = useState('');
  const [prev, setPrev]     = useState(null);
  const [ym, setYm]         = useState({ year: '', month: '' });

  const load = useCallback(async () => {
    setBusy('load');
    try {
      const d = await api(`/api/monthly-report/config?type=${type}`);
      setCfg(d);
      setForm({
        enabled:        !!Number(d.enabled),
        email_to:       d.email_to || '',
        email_cc:       d.email_cc || '',
        email_bcc:      d.email_bcc || '',
        include_aws:    !!Number(d.include_aws),
        include_s3:     !!Number(d.include_s3),
        include_solman: !!Number(d.include_solman),
        include_sdp:    !!Number(d.include_sdp),
        include_m365:   !!Number(d.include_m365),
        include_sdp_assets: !!Number(d.include_sdp_assets),
        include_sap_usage:  !!Number(d.include_sap_usage),
        include_sap_licenses: !!Number(d.include_sap_licenses),
        sap_lic_landscape: d.sap_lic_landscape || '',
        sap_lic_sid:       d.sap_lic_sid || '',
        sap_lic_client:    d.sap_lic_client || '',
        include_charts:     !!Number(d.include_charts),
        resend_every_run:   !!Number(d.resend_every_run),
        // body_mode adalah sumber kebenaran; fallback ke include_body hanya
        // untuk backend yang belum ter-deploy - tanpa itu <select>/radio jadi
        // uncontrolled dan React memperingatkannya.
        body_mode:          d.body_mode || (Number(d.include_body) ? 'full' : 'custom'),
        sender_name:     d.sender_name || '',
        report_title:    d.report_title || '',
        report_subtitle: d.report_subtitle || '',
        intro_text:      d.intro_text || '',
        intro_spacing:   d.intro_spacing || 'normal',
        // Server SELALU mengirim nilai yang sudah diselesaikan (kolom kosong
        // sudah diterjemahkan jadi default tipe oleh getConfig), jadi fallback
        // di sini hanya jaring pengaman kalau backend belum ter-deploy — tanpa
        // itu, <select> jadi uncontrolled dan React memperingatkannya.
        email_text_scale: d.email_text_scale || 'normal',
        email_width:      Number(d.email_width) || 900,
        prepared_by:     d.prepared_by || '',
        ai_enabled:  !!Number(d.ai_enabled),
        ai_provider: d.ai_provider || '',
        ai_model:    d.ai_model || '',
      });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  }, [api, type]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('save'); setAlert(null);
    try {
      // include_body ikut dikirim sebagai turunan body_mode. Server memang
      // memenangkan body_mode bila keduanya ada, tapi mengirim keduanya membuat
      // form ini tetap benar kalau suatu saat dipakai terhadap backend lama.
      const d = await api('/api/monthly-report/config', {
        method: 'PUT',
        body: JSON.stringify({ ...form, include_body: form.body_mode === 'full', type }),
      });
      setCfg(d);
      setAlert({ type: 'success', msg: t('mreport.saved') });
      onChanged?.();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const periodBody = () => JSON.stringify({
    type,
    year:  ym.year  ? Number(ym.year)  : undefined,
    month: ym.month ? Number(ym.month) : undefined,
  });

  const doPreview = async () => {
    setBusy('preview'); setAlert(null); setPrev(null);
    try {
      const d = await api('/api/monthly-report/preview', { method: 'POST', body: periodBody() });
      setPrev(d);
      if (d.skipped) setAlert({ type: 'error', msg: t('mreport.skipped', { reason: d.reason || '-' }) });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const doSend = async () => {
    const to = (cfg && cfg.email_to) || t('mreport.empty');
    if (!window.confirm(t('mreport.sendConfirm', { to }))) return;
    setBusy('send'); setAlert(null);
    try {
      const d = await api('/api/monthly-report/send', { method: 'POST', body: periodBody() });
      if (d.sent) setAlert({ type: 'success',
        msg: t('mreport.sentOk', { period: d.period, to: d.to, size: fmtBytes(d.bytes) }) });
      else setAlert({ type: 'error', msg: t('mreport.notSent', { reason: d.reason || d.error || '-' }) });
      load();
      onChanged?.();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const doXlsx = async () => {
    setBusy('xlsx'); setAlert(null);
    try {
      const q = ['type=' + type];
      if (ym.year)  q.push('year=' + Number(ym.year));
      if (ym.month) q.push('month=' + Number(ym.month));
      const res = await axiosApi.get('/api/monthly-report/xlsx?' + q.join('&'),
        { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m  = cd.match(/filename="?([^"]+)"?/);
      const name = (m && m[1]) || 'monthly-report.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setAlert({ type: 'success', msg: t('mreport.xlsx.done', { name }) });
    } catch (e) {
      setAlert({ type: 'error', msg: (e.response?.data?.error) || e.message });
    } finally { setBusy(''); }
  };

  const card  = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 };
  const label = { fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.03em' };
  const input = { padding: '7px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit' };
  const chk   = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' };

  if (!form) return <Empty icon="⏳" text={t('mreport.loading')} />;

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />

      {/* Cron schedule - switch #1 */}
      {/* jobKey differs per profile; `key` forces a remount so the schedule state
          is not carried over from the previously selected tab. */}
      <CronScheduler key={cfg?.cron_key || type} api={api} jobKey={cfg?.cron_key || 'monthly_report'}
        runConfirm={t('mreport.runConfirm', { type: type.toUpperCase() })} />

      <div style={{ fontSize: 12, color: 'rgb(var(--brand-700))', background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe',
                    borderRadius: 8, padding: '8px 10px', margin: '14px 0' }}>
        <b>{t('mreport.switches.title')}</b> {t('mreport.switches.body')}
      </div>

      {/* Configuration - switch #2 + recipients + modules */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>⚙️ {t('mreport.config')}</div>

        <label style={{ ...chk, marginBottom: 12, fontWeight: 600 }}>
          <input type="checkbox" checked={form.enabled}
            onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
          {t('mreport.enableAuto')}
        </label>

          <label style={{ ...chk, marginBottom: 4, fontWeight: 600 }}>
            <input type="checkbox" checked={!!form.resend_every_run}
              onChange={e => setForm(f => ({ ...f, resend_every_run: e.target.checked }))} />
            {t('mreport.resendEvery')}
          </label>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12, marginLeft: 22 }}>{t('mreport.resendEveryHint')}</div>

        <div style={{ marginBottom: 12 }}>
          <div style={label}>{t('mreport.to')}</div>
          <input type="text" value={form.email_to} placeholder={t('mreport.to.ph')}
            onChange={e => setForm(f => ({ ...f, email_to: e.target.value }))}
            style={{ ...input, width: 480, maxWidth: '100%' }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {t('mreport.to.hint')}
          </div>
        </div>

        {/* CC / BCC - per profile, same normalisation as To (comma separated) */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={label}>{t('mreport.cc')}</div>
              <input type="text" value={form.email_cc} placeholder={t('mreport.cc.ph')}
                onChange={e => setForm(f => ({ ...f, email_cc: e.target.value }))}
                style={{ ...input, width: 320, maxWidth: '100%' }} />
            </div>
            <div>
              <div style={label}>{t('mreport.bcc')}</div>
              <input type="text" value={form.email_bcc} placeholder={t('mreport.bcc.ph')}
                onChange={e => setForm(f => ({ ...f, email_bcc: e.target.value }))}
                style={{ ...input, width: 320, maxWidth: '100%' }} />
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {t('mreport.ccbcc.hint')}
          </div>
        </div>

        {/* Nama tampilan pengirim. Alamatnya sendiri (smtp_from) tetap dari tab
            ASE Alert - dipakai bersama modul lain, jadi tidak per profil. */}
        <div style={{ marginBottom: 12 }}>
          <div style={label}>{t('mreport.senderName')}</div>
          <input type="text" value={form.sender_name} placeholder="Primary Dashboard"
            onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))}
            style={{ ...input, width: 320, maxWidth: '100%' }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {t('mreport.senderName.hint')}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={label}>{t('mreport.modules')}</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={chk}><input type="checkbox" checked={form.include_aws}
              onChange={e => setForm(f => ({ ...f, include_aws: e.target.checked }))} /> AWS Cost</label>
            {/* Modul TERSENDIRI, bukan turunan AWS Cost. Sumbernya AWS Inventory
                (bukan aws_cost_raw), jadi bisa dinyalakan tanpa modul biaya. */}
            <label style={chk}><input type="checkbox" checked={form.include_s3}
              onChange={e => setForm(f => ({ ...f, include_s3: e.target.checked }))} /> AWS S3 Bucket</label>
            <label style={chk}><input type="checkbox" checked={form.include_solman}
              onChange={e => setForm(f => ({ ...f, include_solman: e.target.checked }))} /> Solman Tickets</label>
            <label style={chk}><input type="checkbox" checked={form.include_sdp}
              onChange={e => setForm(f => ({ ...f, include_sdp: e.target.checked }))} /> SDP Cloud</label>
            <label style={chk}><input type="checkbox" checked={form.include_m365}
              onChange={e => setForm(f => ({ ...f, include_m365: e.target.checked }))} /> M365 (snapshot)</label>
            <label style={chk}><input type="checkbox" checked={form.include_sdp_assets}
              disabled={!form.include_sdp}
              onChange={e => setForm(f => ({ ...f, include_sdp_assets: e.target.checked }))} />
              SDP Assets (snapshot)</label>
            <label style={chk}><input type="checkbox" checked={form.include_sap_usage}
              onChange={e => setForm(f => ({ ...f, include_sap_usage: e.target.checked }))} />
              {t('mreport.mod.sapUsage')}</label>
            <label style={chk}><input type="checkbox" checked={form.include_sap_licenses}
              onChange={e => setForm(f => ({ ...f, include_sap_licenses: e.target.checked }))} />
              {t('mreport.mod.sapLicenses')}</label>
          </div>
          {/* Keterangan sumber data tiap modul. Ditulis literal seperti label
              modulnya sendiri, supaya tidak menambah key i18n di id/en/ar. */}
          <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 3 }}>Where each module gets its data</div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>AWS Cost</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>aws_cost_raw</code>{' · '}AWS Cost Explorer, cron aws_cost (daily 02:00). Real usage cost only.
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>AWS S3 Bucket</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>aws_s3_size_history / aws_s3_object_history / aws_s3_backup_stats</code>{' · '}AWS Inventory, cron aws_inventory (daily 03:00). Object detail is limited to the 13 SAP backup buckets. A separate module, not a child of AWS Cost.
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>Solman Tickets</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>solman_tickets</code>{' · '}SAP Solution Manager, cron solman_tickets (daily 05:00).
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>SDP Cloud</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>sdp_requests</code>{' · '}ServiceDesk Plus Cloud, cron sdp (daily 06:00).
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>SDP Assets (snapshot)</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>sdp_assets</code>{' · '}Same cron sdp (daily 06:00). Needs SDP Cloud to be enabled.
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>M365 (snapshot)</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>m365_capacity / m365_user_license</code>{' · '}Microsoft Graph, cron m365_license (daily 06:00).
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>SAP User Utilisation</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>sap_usage</code>{' · '}Excel upload on the SAP User Usage page. No cron — the figures only move when someone uploads.
            </div>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: '#64748b' }}>SAP Licences (snapshot)</b>{' — '}
              <code style={{ fontSize: 10.5, color: '#64748b' }}>sap_um_users × sap_license_types</code>{' · '}SAP User Maintenance snapshot for the connection chosen below. No cron — refilled only when Load Users is pressed.
            </div>
          </div>
        </div>

        {/* Koneksi SAP untuk seksi lisensi. Bukan sap_connections.id numerik -
            id bisa berubah kalau tabel itu di-rebuild, sedangkan
            landscape/SID/client adalah identitas faktual sistemnya. Field
            disembunyikan saat saklarnya mati supaya tidak menambah kebisingan. */}
        {form.include_sap_licenses && (
          <div style={{ marginBottom: 12 }}>
            <div style={label}>{t('mreport.sapLic.conn')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="text" value={form.sap_lic_landscape} placeholder="ID"
                onChange={e => setForm(f => ({ ...f, sap_lic_landscape: e.target.value }))}
                style={{ ...input, width: 110 }} />
              <input type="text" value={form.sap_lic_sid} placeholder="P01"
                onChange={e => setForm(f => ({ ...f, sap_lic_sid: e.target.value }))}
                style={{ ...input, width: 90 }} />
              <input type="text" value={form.sap_lic_client} placeholder="310"
                onChange={e => setForm(f => ({ ...f, sap_lic_client: e.target.value }))}
                style={{ ...input, width: 90 }} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
              {t('mreport.sapLic.hint')}
            </div>
          </div>
        )}

        {/* Bukan modul data, melainkan opsi tampilan - karena itu barisnya sendiri.

            body_mode ada DI ATAS include_charts karena mode non-full membuat
            chart tidak relevan sama sekali: chart hanya hidup di badan email,
            lampiran .xlsx tidak memuatnya. */}
        {/* Tiga keadaan, bukan dua - karena itu radio, bukan checkbox. Label
            sengaja dalam bahasa Inggris langsung (bukan lewat t()): ini opsi
            baru dan i18n tidak dipakai untuk seksi ini. */}
        <div style={{ marginBottom: 12 }}>
          <div style={label}>Email body</div>
          {[
            ['full',   'Full report',
              'Tables, charts, and the AI executive summary when AI is enabled.'],
            ['custom', 'Custom text only',
              'Only the introduction text below. No tables, no charts, no AI.'],
            ['ai',     'Executive summary only (AI)',
              'Introduction text plus the AI summary. Runs the model even when the AI switch below is off.'],
          ].map(([val, title, hint]) => (
            <label key={val} style={{ ...chk, alignItems: 'flex-start', marginBottom: 4 }}>
              <input type="radio" name={`body_mode_${type}`} value={val}
                checked={form.body_mode === val}
                onChange={() => setForm(f => ({ ...f, body_mode: val }))}
                style={{ marginTop: 3 }} />
              <span>
                <span style={{ fontWeight: 600 }}>{title}</span>
                <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
                  {hint}
                </span>
              </span>
            </label>
          ))}
          {form.body_mode !== 'full' && (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb',
                          border: '1px solid #fde68a', borderRadius: 6,
                          padding: '7px 9px', marginTop: 6, lineHeight: 1.6 }}>
              {t('mreport.body.warn')}
              {!String(form.intro_text || '').trim() && ' ' + t('mreport.body.warnNoIntro')}
              {form.body_mode === 'ai'
                && ' If the model fails, the email is still sent with the attachment'
                   + ' and a note that the summary is missing.'}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12, opacity: form.body_mode === 'full' ? 1 : 0.45 }}>
          <label style={{ ...chk, fontWeight: 600 }}>
            <input type="checkbox" checked={form.include_charts} disabled={form.body_mode !== 'full'}
              onChange={e => setForm(f => ({ ...f, include_charts: e.target.checked }))} />
            {t('mreport.charts')}
          </label>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {t('mreport.charts.hint')}
          </div>
        </div>

        {/* Tampilan email - per profil. Bukan preferensi kosmetik: klien email
            tidak punya zoom yang bisa diandalkan (Outlook desktop merender lewat
            mesin Word), jadi ukuran harus dipilih di sisi pengirim. */}
        <div style={{ borderTop: '1px solid #e2e8f0', margin: '14px 0', paddingTop: 14 }}>
          <div style={label}>{t('mreport.layout')}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ ...label, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                {t('mreport.layout.scale')}
              </div>
              <select value={form.email_text_scale}
                onChange={e => setForm(f => ({ ...f, email_text_scale: e.target.value }))}
                style={{ ...input, width: 260 }}>
                <option value="compact">{t('mreport.layout.scale.compact')}</option>
                <option value="normal">{t('mreport.layout.scale.normal')}</option>
                <option value="large">{t('mreport.layout.scale.large')}</option>
              </select>
            </div>
            <div>
              <div style={{ ...label, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                {t('mreport.layout.width')}
              </div>
              {/* Daftar HARUS sama dengan CONTENT_WIDTHS di monthlyReport.js.
                  Nilai di luar daftar itu ditolak diam-diam oleh server dan
                  dikembalikan ke default tipe - jadi angka bebas di sini hanya
                  akan terlihat seperti simpan yang gagal. */}
              <select value={form.email_width}
                onChange={e => setForm(f => ({ ...f, email_width: Number(e.target.value) }))}
                style={{ ...input, width: 160 }}>
                {[720, 800, 900, 1000, 1100].map(w => (
                  <option key={w} value={w}>{w} px{w === 900 ? ' ·' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, lineHeight: 1.6 }}>
            {t('mreport.layout.hint')}
            {form.body_mode !== 'full' && ' ' + t('mreport.layout.bodyOff')}
          </div>
        </div>

        {/* Pengantar - dirender DI ATAS judul, jadi ditaruh di atas blok judul
            supaya urutan di form sama dengan urutan di email. */}
        <div style={{ borderTop: '1px solid #e2e8f0', margin: '14px 0', paddingTop: 14 }}>
          <div style={label}>{t('mreport.intro')}</div>
          <IntroEditor
            value={form.intro_text}
            spacing={form.intro_spacing}
            onChange={v => setForm(f => ({ ...f, intro_text: v }))}
            onSpacing={v => setForm(f => ({ ...f, intro_spacing: v }))}
            t={t} inputStyle={input} labelStyle={label} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.6 }}>
            {t('mreport.intro.hint')}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', margin: '14px 0', paddingTop: 14 }}>
          <div style={label}>{t('mreport.header')}</div>
          <input type="text" value={form.report_title} placeholder={t('mreport.header.titlePh')}
            onChange={e => setForm(f => ({ ...f, report_title: e.target.value }))}
            style={{ ...input, width: 420, maxWidth: '100%', marginBottom: 6 }} />
          <input type="text" value={form.report_subtitle} placeholder={t('mreport.header.subtitlePh')}
            onChange={e => setForm(f => ({ ...f, report_subtitle: e.target.value }))}
            style={{ ...input, width: 420, maxWidth: '100%', marginBottom: 6 }} />
          <input type="text" value={form.prepared_by} placeholder={t('mreport.header.preparedPh')}
            onChange={e => setForm(f => ({ ...f, prepared_by: e.target.value }))}
            style={{ ...input, width: 420, maxWidth: '100%' }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {t('mreport.header.hint')}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', margin: '14px 0', paddingTop: 14 }}>
          <label style={{ ...chk, fontWeight: 600, marginBottom: 8 }}>
            <input type="checkbox" checked={form.ai_enabled}
              onChange={e => setForm(f => ({ ...f, ai_enabled: e.target.checked }))} />
            {t('mreport.ai.enable')}
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input type="text" value={form.ai_provider} placeholder={t('mreport.ai.providerPh')}
              onChange={e => setForm(f => ({ ...f, ai_provider: e.target.value }))}
              style={{ ...input, width: 200 }} />
            <input type="text" value={form.ai_model} placeholder={t('mreport.ai.modelPh')}
              onChange={e => setForm(f => ({ ...f, ai_model: e.target.value }))}
              style={{ ...input, width: 280 }} />
          </div>
          <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, lineHeight: 1.6 }}>
            {t('mreport.ai.warn')}
          </div>
        </div>

        <Btn size="sm" variant="primary" disabled={!!busy} onClick={save}>
          {busy === 'save' ? `⏳ ${t('mreport.saving')}` : `💾 ${t('mreport.save')}`}
        </Btn>

        {cfg && (cfg.last_sent_period || cfg.last_status) && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
            {t('mreport.lastSent')}: <b>{cfg.last_sent_period || '—'}</b>
            {cfg.last_status ? ` · ${t('mreport.status')} ${cfg.last_status}` : ''}
          </div>
        )}
      </div>

      {/* Manual test */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>🧪 {t('mreport.test')}</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={label}>{t('mreport.year')}</div>
            <input type="number" value={ym.year} placeholder="2026"
              onChange={e => setYm(s => ({ ...s, year: e.target.value }))} style={{ ...input, width: 110 }} />
          </div>
          <div>
            <div style={label}>{t('mreport.month')}</div>
            <input type="number" min={1} max={12} value={ym.month} placeholder="6"
              onChange={e => setYm(s => ({ ...s, month: e.target.value }))} style={{ ...input, width: 110 }} />
          </div>
          <Btn size="sm" disabled={!!busy} onClick={doPreview}>
            {busy === 'preview' ? `⏳ ${t('mreport.preview.busy')}` : `👁️ ${t('mreport.preview')}`}
          </Btn>
          <Btn size="sm" disabled={!!busy} onClick={doXlsx}>
            {busy === 'xlsx' ? `⏳ ${t('mreport.xlsx.busy')}` : `⬇️ ${t('mreport.xlsx')}`}
          </Btn>
          <Btn size="sm" variant="danger" disabled={!!busy} onClick={doSend}>
            {busy === 'send' ? `⏳ ${t('mreport.send.busy')}` : `📧 ${t('mreport.send')}`}
          </Btn>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          {t('mreport.test.hint')}
        </div>
      </div>

      {/* Preview result */}
      {prev && !prev.skipped && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>📄 {t('mreport.previewCard')}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            {t('mreport.period')} <b>{prev.period}</b> · {t('mreport.subject')}: <b>{prev.subject}</b>
            {prev.status ? <> · {t('mreport.status')}: <b>{prev.status}</b></> : null}
            {prev.bytes !== undefined ? <> · {t('mreport.attachment')}: <b>{fmtBytes(prev.bytes)}</b></> : null}
          </div>
          <iframe title="preview" srcDoc={prev.html}
            style={{ width: '100%', height: 620, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}


// -- Page --------------------------------------------------------------------

// One profile per type. `cronKey` is display only; the real value comes from the
// server (column cron_key) so there is never a second source of truth.
const REPORT_TABS = [
  { type: 'sap',   label: 'SAP',       icon: '🧩', hintKey: 'mreport.tab.sap.hint' },
  { type: 'infra', label: 'Infra',     icon: '🖧',  hintKey: 'mreport.tab.infra.hint' },
  { type: 'aws',   label: 'AWS',       icon: '☁️', hintKey: 'mreport.tab.aws.hint' },
  // Tab tersendiri, bukan checkbox di tab AWS: penerima dan jadwalnya berbeda —
  // storage backup dibaca tim Basis/infra, bukan pemilik anggaran.
  { type: 's3',    label: 'AWS S3 Bucket', icon: '🗄️', hintKey: 'mreport.tab.s3.hint' },
  { type: 'exec',  label: 'Executive', icon: '📈', hintKey: 'mreport.tab.exec.hint' },
  { type: 'sapusage', label: 'SAP Usage (Trimester)', icon: '📊', hintKey: 'mreport.tab.sapusage.hint' },
  // Laporan HARIAN (bukan bulanan): panel & backend sendiri — vpnDailyReport.js.
  { type: 'vpn', label: 'VPN Tunnel (Daily)', icon: '🔐', hint: 'Daily VPN/GRE tunnel status — degrade events + availability · cron job vpn_daily_report' },
];

export default function MonthlyReport() {
  const api = useApi();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  // Tab halaman ini adalah `type`, dan daftar sahnya REPORT_TABS — bukan konstanta
  // terpisah, supaya tidak ada sumber kebenaran kedua.
  const [type, setType] = useState(() => {
    const p = searchParams.get('tab');
    return REPORT_TABS.some(x => x.type === p) ? p : 'sap';
  });
  useTabDeepLink('monthly_report', (p) => REPORT_TABS.some(x => x.type === p), setType);
  const [types, setTypes] = useState([]);

  // Profile summary, used to mark the tabs whose schedule is active.
  const loadTypes = useCallback(async () => {
    try {
      const d = await api('/api/monthly-report/profiles');
      setTypes(d.types || []);
    } catch (_) { setTypes([]); }
  }, [api]);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  const title  = t('mreport.title');
  const sub    = t('mreport.subtitle');
  const meta   = types.find(x => x.type === type);
  const active = REPORT_TABS.find(x => x.type === type);

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>📧 {title}</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 0 }}>{sub}</p>
      </div>

      {/* Report type tabs - each has its own header, recipients, modules and schedule */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', flexWrap: 'wrap',
                    borderBottom: '1px solid #e2e8f0' }}>
        {REPORT_TABS.map(tab => {
          const st = types.find(x => x.type === tab.type);
          const on = type === tab.type;
          return (
            <button key={tab.type} onClick={() => setType(tab.type)}
              style={{ padding: '9px 16px', fontSize: 13, cursor: 'pointer', border: 'none',
                borderBottom: on ? '2px solid rgb(var(--brand-600))' : '2px solid transparent', background: 'none',
                color: on ? 'rgb(var(--brand-600))' : '#64748b', fontWeight: on ? 700 : 500,
                fontFamily: 'inherit', marginBottom: -1, whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {tab.icon} {tab.label}
              {st && (
                <span title={st.enabled ? t('mreport.schedOn') : t('mreport.schedOff')}
                  style={{ width: 7, height: 7, borderRadius: '50%',
                           background: st.enabled ? '#10b981' : '#cbd5e1' }} />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
        {t('mreport.profile')} <b>{active?.label}</b> — {active ? (active.hint || t(active.hintKey)) : ''}
        {meta?.cron_key ? <> · {t('mreport.cronJob')} <code>{meta.cron_key}</code></> : null}
        {meta?.last_sent_period ? <> · {t('mreport.lastSentShort')} <b>{meta.last_sent_period}</b></> : null}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {/* `key` forces the panel to be rebuilt when the type changes - prevents
            the previous profile's form from flashing before the new data loads. */}
        {type === 'vpn'
          ? <VpnDailyReportPanel />
          : type === 'sapusage'
          ? <SapUsagePeriodicPanel />
          : <MonthlyReportPanel key={type} api={api} type={type} onChanged={loadTypes} />}
      </div>
    </div>
  );
}
